import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Chip, Avatar, Paper, CircularProgress, Dialog, TextField, Popover } from '@mui/material';
import { ThumbUp, ThumbDown, CheckCircle } from '@mui/icons-material';
import AIIcon from './AIIcon';
import CandidateProfileSidebar from './CandidateProfileSidebar';
import LoadingCandidateCard from './LoadingCandidateCard';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';

/**
 * ReviewMatchesTabV2 - Queue-Aware Version
 * 
 * This version is properly integrated with the centralized discovery queue system:
 * - Uses handleRefreshResultsFromQueue for queue-aware API calls
 * - Doesn't trigger legacy fetchMatches automatically 
 * - Integrates candidate feedback through the queue system
 * - No conflicting sessionStorage or legacy state management
 */
const ReviewMatchesTabV2 = ({ 
  profiles, 
  macroMappingData,
  onCandidateApproval,
  handleRefreshResultsFromQueue, // Queue-aware refresh function
  fetchMatches, // Queue-aware fetch function for candidate profiles
  setMessages, // For adding feedback messages
  onRefreshResults // For triggering search refresh with feedback
}) => {
  // Core state
  const [approvedCandidates, setApprovedCandidates] = useState(new Set());
  const [rejectedCandidates, setRejectedCandidates] = useState(new Set());
  
  // Candidate Profile Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Candidate pagination state
  const [currentCandidateOffset, setCurrentCandidateOffset] = useState(0);

  // Loading state for fetching profiles
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

  // Thumbs down modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedCandidateForReason, setSelectedCandidateForReason] = useState(null);

  // Popover state for matching criteria
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverCriteria, setPopoverCriteria] = useState([]);

  // Queue-aware profile fetching - only when explicitly needed
  const handleFetchProfiles = useCallback(async () => {
    // Check if we have completed queue items first
    const allQueueItems = centralDiscoveryQueue.getAllItems();
    const completedItems = allQueueItems.filter(item => item.disco_done === true);
    
    if (completedItems.length === 0) {
      console.log('ReviewMatchesTabV2: No completed queue items - cannot fetch profiles');
      return;
    }

    console.log('ReviewMatchesTabV2: Fetching profiles using queue-aware approach');
    setIsLoadingProfiles(true);
    
    try {
      // First: Get match counts using queue-aware refresh function
      await handleRefreshResultsFromQueue(completedItems);
      console.log('ReviewMatchesTabV2: Match counts updated via queue system');
      
      // Second: Fetch actual candidate profiles using queue-aware fetchMatches
      if (fetchMatches) {
        console.log('ReviewMatchesTabV2: Calling fetchMatches with completed queue items');
        await fetchMatches(completedItems); // ✅ Pass queue items to use centralized queue approach
        console.log('ReviewMatchesTabV2: Candidate profiles fetched via queue system');
      }
    } catch (error) {
      console.error('ReviewMatchesTabV2: Error fetching profiles via queue system:', error);
    } finally {
      setIsLoadingProfiles(false);
    }
  }, [handleRefreshResultsFromQueue, fetchMatches]);

  // Auto-fetch profiles when switching to this tab and no profiles exist
  useEffect(() => {
    const shouldFetchProfiles = !profiles || (profiles && Object.keys(profiles).length === 0);
    
    if (shouldFetchProfiles && !isLoadingProfiles) {
      // Check if we have completed queue items first
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      
      if (completedItems.length > 0) {
        console.log('ReviewMatchesTabV2: Found completed queue items, auto-fetching profiles');
        handleFetchProfiles();
      } else {
        console.log('ReviewMatchesTabV2: No completed queue items, waiting for user interaction');
      }
    }
  }, [profiles, isLoadingProfiles, handleFetchProfiles]); // Include dependencies to react to changes

  // Convert profiles object to array for easier handling
  const candidateList = profiles ? Object.entries(profiles).map(([id, profile]) => {
    // Extract data from the candidate object
    const name = profile?.candidate?.full_name ? decodeURIComponent(profile.candidate.full_name) : 'Unknown Candidate';
    
    // Get current title
    const title = profile?.workexp
      ?.find(exp => exp.duration?.to_present)
      ?.projects?.map(proj => proj.role_and_group?.title)
      .filter(Boolean)[0] || 'Unknown Title';
    
    // Get company name
    const company = profile?.workexp
      ?.find(exp => exp.duration?.to_present)
      ?.company?.name || 'Unknown Company';
    
    // Get location
    const city = profile?.candidate?.location?.city || '';
    const state = profile?.candidate?.location?.state || '';
    const country = profile?.candidate?.location?.country || '';
    
    // Format location as "City, State, Country"
    let location = '';
    if (city && state && country) {
      location = `${city}, ${state}, ${country}`;
    } else if (city && country) {
      location = `${city}, ${country}`;
    } else if (city) {
      location = city;
    } else {
      location = 'Unknown Location';
    }

    return {
      id,
      profile,
      name,
      title,
      company,
      location,
      first_name: name.split(' ')[0] || 'Unknown',
      last_name: name.split(' ').slice(1).join(' ') || '',
    };
  }) : [];

  const handleApproval = (candidateId, approved) => {
    const newApproved = new Set(approvedCandidates);
    const newRejected = new Set(rejectedCandidates);
    
    if (approved) {
      newApproved.add(candidateId);
      newRejected.delete(candidateId);
    } else {
      newRejected.add(candidateId);
      newApproved.delete(candidateId);
    }
    
    setApprovedCandidates(newApproved);
    setRejectedCandidates(newRejected);
    
    if (onCandidateApproval) {
      onCandidateApproval(candidateId, approved);
    }

    // Auto-navigate to messaging flow after 5 approvals
    if (newApproved.size === 5) {
      console.log('ReviewMatchesTabV2: 5 candidates approved, auto-navigating to messaging flow');
      setTimeout(() => {
        // Switch the content tab
        if (window.switchToMessagingTab && typeof window.switchToMessagingTab === 'function') {
          window.switchToMessagingTab();
        }
        
        // Also trigger the header navigation to update discovery state
        if (window.onNavigateToSendOutreach && typeof window.onNavigateToSendOutreach === 'function') {
          window.onNavigateToSendOutreach();
        }
      }, 500);
    }
  };

  // Handle candidate name click to open sidebar
  const handleCandidateNameClick = (candidate) => {
    setSelectedCandidate(candidate.profile);
    setSelectedCandidateId(candidate.id);
    setSidebarOpen(true);
  };

  // Handle sidebar close
  const handleSidebarClose = () => {
    setSidebarOpen(false);
    setSelectedCandidate(null);
    setSelectedCandidateId(null);
  };

  // Thumbs down handlers
  const handleThumbsDown = (candidate) => {
    setSelectedCandidateForReason(candidate);
    setShowReasonModal(true);
    // Set the candidate as rejected
    handleApproval(candidate.id, false);
  };

  const handleReasonSubmit = () => {
    if (reason.trim() && selectedCandidateForReason) {
      // Create user message and add it directly to the messages array
      const userMessage = {
        role: 'User',
        content: `I don't like candidate ${selectedCandidateForReason.name} because: ${reason.trim()}`,
        id: `user_candidate_feedback_${Date.now()}`
      };
      
      // Add message to the messages array first, then trigger refresh
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = [...prev, userMessage];
          
          // Trigger search refresh with the complete updated messages array
          if (onRefreshResults) {
            console.log('ReviewMatchesTabV2: Triggering onRefreshResults for candidate:', selectedCandidateForReason.name);
            console.log('ReviewMatchesTabV2: Sending full message history with', updatedMessages.length, 'messages');
            // Use setTimeout to ensure the state update is processed
            setTimeout(() => {
              onRefreshResults(updatedMessages);
            }, 0);
          } else {
            console.warn('ReviewMatchesTabV2: onRefreshResults callback not available');
          }
          
          return updatedMessages;
        });
      }
    }
    
    setShowReasonModal(false);
    setReason('');
    setSelectedCandidateForReason(null);
  };

  const handleReasonCancel = () => {
    setShowReasonModal(false);
    setReason('');
    // Remove rejection if user cancels
    if (selectedCandidateForReason) {
      const newRejected = new Set(rejectedCandidates);
      newRejected.delete(selectedCandidateForReason.id);
      setRejectedCandidates(newRejected);
    }
    setSelectedCandidateForReason(null);
  };

  // Popover handlers for matching criteria
  const handlePopoverOpen = (event, criteria) => {
    setPopoverAnchorEl(event.currentTarget);
    setPopoverCriteria(criteria);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setPopoverCriteria([]);
  };

  const isPopoverOpen = Boolean(popoverAnchorEl);

  // Helper functions for parsing matching criteria (simplified from original)
  function parseQueryElements(queryString) {
    if (!queryString) return [];
    
    let cleanQuery = queryString.trim();
    if (cleanQuery.startsWith('(') && cleanQuery.endsWith(')')) {
      cleanQuery = cleanQuery.slice(1, -1);
    }
    
    const elements = cleanQuery
      .split(/\s+(?:OR|AND)\s+/)
      .map(element => element.trim())
      .filter(element => element.length > 0)
      .map(element => {
        return element.replace(/^['"`(]+|['"`)]+$/g, '').trim();
      })
      .filter(element => element.length > 0);
    
    return elements;
  }

  function getReadableName(element, queries) {
    // If it's a hex ID, try to look it up
    if (/^[a-f0-9]{24}$/i.test(element) && queries && queries[element]) {
      return queries[element].name || element;
    }
    
    // If it contains a colon, extract the value part
    if (element.includes(':')) {
      const parts = element.split(':');
      if (parts.length > 1) {
        let value = parts[parts.length - 1].trim();
        value = value.replace(/^['"`(]+|['"`)]+$/g, '');
        return value;
      }
    }
    
    return element;
  }

  // Get matching criteria for a candidate
  function getMatchingCriteria(profileId, macroMappingData) {
    if (!macroMappingData || !macroMappingData.queryResults || !macroMappingData.autoMacroMap) {
      return [];
    }

    const queryResult = macroMappingData.queryResults.find(result => result.profile_id === profileId);
    if (!queryResult || !queryResult.macro_ids) {
      return [];
    }

    const allCriteria = [];
    
    queryResult.macro_ids.forEach(macroId => {
      const macroValue = macroMappingData.autoMacroMap[macroId];
      if (!macroValue) return;

      const elements = parseQueryElements(macroValue);
      
      elements.forEach(element => {
        const readableName = getReadableName(element, macroMappingData.queries);
        
        if (readableName && 
            readableName.length > 2 && 
            !readableName.startsWith('profile_type:') &&
            !/^[a-f0-9]{24}$/i.test(readableName)) {
          allCriteria.push(readableName);
        }
      });
    });

    return [...new Set(allCriteria)];
  }

  // Get current visible candidates based on offset
  const visibleCandidates = candidateList.slice(currentCandidateOffset, currentCandidateOffset + 5);
  
  // Handle loading next 5 candidates
  const handleLoadNextCandidates = () => {
    const nextOffset = currentCandidateOffset + 5;
    if (nextOffset < candidateList.length) {
      setCurrentCandidateOffset(nextOffset);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '40px',
          paddingBottom: '120px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FAFB',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E7EB',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#D1D5DB',
          }
        }}
      >
        {/* Chat Wrapper */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '32px',
            padding: '24px 10px 10px',
          }}
        >
        {/* AI Message */}
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            width: '616px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: 20,
              minWidth: 20,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '4px',
            }}
          >
            <AIIcon size={40} />
          </Box>
          
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <Paper
              sx={{
                padding: '0px 8px',
                borderRadius: '10px 10px 10px 0px',
                backgroundColor: '#FFFFFF',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  lineHeight: '1.375em',
                  color: '#101828',
                }}
              >
                Let's review a few candidate matches to make sure we're on the same page.
                <br />
                I'll need you to approve 5 matching candidates before we can kick off outreach 🚀
                <br />
                Ready when you are! 👀✅
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Candidate List */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '616px',
          }}
        >
          {/* Show loading cards if profiles are being fetched */}
          {isLoadingProfiles && candidateList.length === 0 ? (
            [...Array(5)].map((_, index) => (
              <LoadingCandidateCard key={`loading-${index}`} />
            ))
          ) : candidateList.length === 0 ? (
            /* No profiles and not loading - show manual fetch option */
            <Paper
              sx={{
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #DCDFEA',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  color: '#667085',
                  textAlign: 'center',
                }}
              >
                No candidate profiles loaded yet. Make sure you've completed your requirements first.
              </Typography>
              <Button
                variant="contained"
                onClick={handleFetchProfiles}
                disabled={isLoadingProfiles}
                sx={{
                  backgroundColor: '#4599FA',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
              >
                {isLoadingProfiles ? <CircularProgress size={20} /> : 'Load Candidate Profiles'}
              </Button>
            </Paper>
          ) : (
            /* Show candidate cards */
            visibleCandidates.map((candidate) => {
            const isApproved = approvedCandidates.has(candidate.id);
            const isRejected = rejectedCandidates.has(candidate.id);
            const matchingCriteria = getMatchingCriteria(candidate.id, macroMappingData);
            
            return (
              <Paper
                key={candidate.id}
                sx={{
                  padding: '24px 16px',
                  borderRadius: '12px',
                  border: '1px solid #DCDFEA',
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {/* Candidate Header */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Candidate Info */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '16px',
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'flex-start',
                      }}
                    >
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: '66px',
                          height: '66px',
                          backgroundColor: '#C5E0FF',
                        }}
                        src={candidate.profile_pic_url}
                      >
                        {candidate.first_name?.[0]}{candidate.last_name?.[0]}
                      </Avatar>
                      
                      {/* Basic Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          width: '440px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1px',
                          }}
                        >
                          {/* Name */}
                          <Typography
                            onClick={() => handleCandidateNameClick(candidate)}
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '18px',
                              fontWeight: 500,
                              lineHeight: '1.333em',
                              color: '#6750A4',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#5a3d99',
                              },
                            }}
                          >
                            {candidate.first_name} {candidate.last_name}
                          </Typography>
                          
                          {/* Current Job */}
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              lineHeight: '1.429em',
                              color: '#465366',
                            }}
                          >
                            {candidate.title} at {candidate.company}
                          </Typography>
                          
                          {/* Location */}
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              lineHeight: '1.429em',
                              color: '#667085',
                            }}
                          >
                            {candidate.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <IconButton
                      onClick={() => handleThumbsDown(candidate)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        border: '1px solid #DCDFEA',
                        borderRadius: '8px',
                        backgroundColor: isRejected ? '#FEF3F2' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#FEF3F2',
                        },
                      }}
                    >
                      <ThumbDown
                        sx={{
                          fontSize: '18px',
                          color: isRejected ? '#B42318' : '#465366',
                        }}
                      />
                    </IconButton>
                    
                    <IconButton
                      onClick={() => handleApproval(candidate.id, true)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        border: '1px solid #DCDFEA',
                        borderRadius: '8px',
                        backgroundColor: isApproved ? '#E0FFE9' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#E0FFE9',
                        },
                      }}
                    >
                      <ThumbUp
                        sx={{
                          fontSize: '18px',
                          color: isApproved ? '#207C39' : '#465366',
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
                
                {/* Matching Criteria Chips */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  {matchingCriteria.slice(0, 4).map((criteria, index) => (
                    <Chip
                      key={index}
                      icon={<CheckCircle sx={{ fontSize: '14px', color: '#207C39' }} />}
                      label={criteria}
                      sx={{
                        backgroundColor: '#E0FFE9',
                        color: '#207C39',
                        fontSize: '12px',
                        fontFamily: 'Roboto',
                        height: '24px',
                        borderRadius: '16px',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                          color: '#207C39',
                        },
                      }}
                    />
                  ))}
                  {matchingCriteria.length > 4 && (
                    <Chip
                      label={`+${matchingCriteria.length - 4} more`}
                      onMouseEnter={(event) => handlePopoverOpen(event, matchingCriteria.slice(4))}
                      onMouseLeave={handlePopoverClose}
                      sx={{
                        backgroundColor: '#F3F4F6',
                        color: '#6B7280',
                        fontSize: '12px',
                        fontFamily: 'Roboto',
                        height: '24px',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#E5E7EB',
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            );
          }))}
        </Box>

        {/* Load Next Candidates Button */}
        {currentCandidateOffset + 5 < candidateList.length && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '616px',
              marginTop: '20px',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleLoadNextCandidates}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontFamily: 'Roboto',
                fontWeight: 500,
                borderColor: '#6750A4',
                color: '#6750A4',
                backgroundColor: '#FFFFFF',
                padding: '12px 24px',
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#5a3d99',
                  backgroundColor: '#f0f7ff',
                  color: '#5a3d99',
                },
              }}
            >
              Load Next Candidates ({Math.min(5, candidateList.length - currentCandidateOffset - 5)} more)
            </Button>
          </Box>
        )}
        </Box>
      </Box>

      {/* Progress Indicator */}
      <Paper
        sx={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #DCDFEA',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '1.429em',
            color: '#101828',
          }}
        >
          Approve 5 to continue
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {[...Array(5)].map((_, index) => (
            <Box
              key={index}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: index < approvedCandidates.size ? '#00B093' : '#F3F5F8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {index < approvedCandidates.size && (
                <ThumbUp sx={{ fontSize: '12px', color: '#FFFFFF' }} />
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Candidate Profile Sidebar */}
      {sidebarOpen && selectedCandidate && (
        <CandidateProfileSidebar
          candidate={selectedCandidate}
          profileId={selectedCandidateId}
          open={sidebarOpen}
          onClose={handleSidebarClose}
          macroMappingData={macroMappingData}
        />
      )}

      {/* Thumbs Down Reason Modal */}
      <Dialog
        open={showReasonModal}
        onClose={handleReasonCancel}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '0px',
            boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
            width: '560px',
            margin: '0px',
          }
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'stretch',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            padding: '24px 32px',
            borderBottom: '1px solid #EAECF0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                width: '100%',
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #DCDFEA',
                  borderRadius: '6px',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Material Icons Round',
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '1em',
                    color: '#465366',
                  }}
                >
                  edit_note
                </Typography>
              </Box>
              
              {/* Text */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: '24px',
                    color: '#313131',
                  }}
                >
                  Candidate Feedback
                </Typography>
                
                {/* Subtitle */}
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#465366',
                  }}
                >
                  Help us understand what didn't quite fit with {selectedCandidateForReason?.name || 'this candidate'}
                </Typography>
              </Box>
            </Box>
            
            {/* Close Button */}
            <IconButton
              onClick={handleReasonCancel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 12px',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                color: '#667085',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Material Icons Round',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '1em',
                  color: '#667085',
                }}
              >
                close
              </Typography>
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            gap: '20px',
            padding: '32px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
              gap: '8px',
              backgroundColor: '#FFFFFF',
            }}
          >
            {/* Feedback Label */}
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#101828',
              }}
            >
              Feedback:
            </Typography>
            
            {/* Input */}
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="e.g., Missing required Python experience, location doesn't work, etc."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #DCDFEA',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#DCDFEA',
                  },
                  '&.Mui-focused': {
                    borderColor: '#DCDFEA',
                  },
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#667085',
                  padding: '0px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#667085',
                  opacity: 1,
                }
              }}
            />
          </Box>
        </Box>

        {/* Modal Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'stretch',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            padding: '0px 32px 24px 32px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
              }}
            >
              {/* Cancel Button */}
              <Button
                onClick={handleReasonCancel}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #DCDFEA',
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                    borderColor: '#DCDFEA',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    textAlign: 'center',
                    color: '#101828',
                  }}
                >
                  Cancel
                </Typography>
              </Button>
              
              {/* Submit Feedback Button */}
              <Button
                onClick={handleReasonSubmit}
                disabled={!reason.trim()}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: '#4599FA',
                  borderRadius: '6px',
                  textTransform: 'none',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#3A7BD8',
                  },
                  '&:disabled': {
                    backgroundColor: '#F2F4F7',
                    color: '#667085',
                  }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    textAlign: 'center',
                    color: reason.trim() ? '#FFFFFF' : '#667085',
                  }}
                >
                  Submit Feedback
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Matching Criteria Popover */}
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            maxWidth: '300px',
            pointerEvents: 'auto',
          }
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '8px',
          }}
        >
          All Matching Attributes
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {popoverCriteria.map((criteria, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <CheckCircle sx={{ fontSize: '14px', color: '#207C39' }} />
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  color: '#207C39',
                }}
              >
                {criteria}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default ReviewMatchesTabV2;